import { useCallback, useEffect, useRef, useState } from 'react';

import { clearDraft, readDraft, saveDraft } from './draft';

export interface ChatDraft {
  value: string;
  set: (value: string) => void;
  clear: () => void;
  /**
   * Вернуть неотправленный текст в поле — только если поле пусто: набранное
   * после отправки новое сообщение важнее старого и не затирается.
   */
  restore: (value: string) => void;
}

/**
 * Черновик сообщения, переживающий уход со страницы.
 *
 * Состоянием владеет React, `sessionStorage` — только зеркало: набранный текст
 * не должен теряться при переходе на экран критериев и обратно. При смене
 * сессии (сброс подбора) черновик перечитывается — чужой текст в новом диалоге
 * не всплывает.
 */
export function useChatDraft(sessionId: string | null): ChatDraft {
  const [value, setValue] = useState(() => (sessionId ? readDraft(sessionId) : ''));
  const loadedFor = useRef(sessionId);

  useEffect(() => {
    if (loadedFor.current === sessionId) return;
    loadedFor.current = sessionId;
    setValue(sessionId ? readDraft(sessionId) : '');
  }, [sessionId]);

  const set = useCallback(
    (next: string) => {
      setValue(next);
      if (sessionId) saveDraft(sessionId, next);
    },
    [sessionId],
  );

  const clear = useCallback(() => {
    setValue('');
    if (sessionId) clearDraft(sessionId);
  }, [sessionId]);

  const restore = useCallback(
    (text: string) => {
      setValue((current) => {
        if (current.trim() !== '') return current;
        if (sessionId) saveDraft(sessionId, text);
        return text;
      });
    },
    [sessionId],
  );

  return { value, set, clear, restore };
}
